import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

// Importaciones de entornos.
import { environment } from '../../environments/environment';

// Importaciones de interfaces.
import { IProduct } from '../interfaces/product.interface';

/**
 * Servicio encargado de gestionar la información y validación de productos en la aplicación.
 *
 * Este servicio incluye funcionalidades como la obtención de productos, creación, actualización
 * y posiblemente eliminación de productos, entre otras tareas relacionadas con la gestión de productos.
 *
 * `@Injectable` permite la inyección de este servicio en otros componentes o servicios.
 * `providedIn: 'root'` garantiza que el servicio esté disponible en toda la aplicación como un singleton,
 * lo que significa que solo habrá una instancia de este servicio en toda la app.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductService {
  /**
   * URL del endpoint para acceder a los productos.
   *
   * Esta propiedad almacena la URL a la que se enviarán las solicitudes HTTP
   * para obtener la lista de productos disponibles en la aplicación.
   */
  private url: string = '/products';

  /**
   * Constructor de la clase ProductService.
   *
   * Inyecta el servicio `HttpClient`, que se utiliza para realizar solicitudes HTTP
   * al servidor de la API y gestionar las operaciones relacionadas con los productos.
   *
   * @param http - Servicio de Angular para realizar solicitudes HTTP.
   */
  constructor(private http: HttpClient) {}

  /**
   * Obtiene una lista de productos con filtros opcionales.
   *
   * @param disabled - Filtra los productos según su estado (activo/inactivo). `null` para ignorar el filtro.
   * @param offset - Define el desplazamiento para la paginación. `null` si no se usa paginación.
   * @param limit - Establece el número máximo de productos a devolver. `null` para no limitar los resultados.
   *
   * @returns Una promesa que se resuelve con la lista de productos.
   */
  public getAll(
    disabled: number | null = null,
    offset: number | null = null,
    limit: number | null = null
  ): Promise<any> {
    const queryParams = { disabled, offset, limit };

    const params = Object.entries(queryParams).reduce(
      (acc, [key, value]) =>
        value !== null ? acc.set(key, value.toString()) : acc,
      new HttpParams()
    );

    return firstValueFrom(
      this.http.get(environment.apiUrl + this.url, { params })
    );
  }

  /**
   * Obtiene los detalles de un producto específico por su ID.
   *
   * @param productId - El identificador único del producto a recuperar.
   * @returns Una promesa que se resuelve con los datos del producto.
   */
  public get(productId: number): Promise<any> {
    return firstValueFrom(
      this.http.get(`${environment.apiUrl + this.url}/${productId}`)
    );
  }

  /**
   * Método para crear un nuevo registro de producto en la API.
   * Realiza una solicitud HTTP POST para enviar los datos del producto al servidor.
   *
   * @param product - Objeto que contiene la información del producto a ser creado.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la creación del producto.
   */
  create(product: IProduct): Promise<any> {
    const formData = this.buildFormData(product);

    return firstValueFrom(
      this.http.post(environment.apiUrl + this.url, formData)
    );
  }

  /**
   * Método para actualizar un registro del producto en la API.
   * Realiza una solicitud HTTP PUT para enviar los datos actualizados del producto al servidor.
   *
   * @param product - Objeto que contiene la información del producto con los cambios a ser actualizados.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la actualización del producto.
   */
  update(product: IProduct): Promise<any> {
    const formData = this.buildFormData(product);

    return firstValueFrom(
      this.http.put(`${environment.apiUrl + this.url}/${product.id}`, formData)
    );
  }

  /**
   * Envía una solicitud PATCH al servidor para actualizar un producto existente.
   *
   * Este método convierte el objeto del producto en un FormData utilizando `buildFormData`,
   * luego realiza una solicitud HTTP PATCH a la URL del producto en el backend.
   * Se utiliza `firstValueFrom` para convertir el observable de Angular en una promesa.
   *
   * @param product - Objeto que representa el producto a actualizar. Debe contener al menos un `id`.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor.
   */
  patch(product: IProduct): Promise<any> {
    const formData = this.buildFormData(product);

    return firstValueFrom(
      this.http.patch(
        `${environment.apiUrl + this.url}/${product.id}`,
        formData
      )
    );
  }

  /**
   * Método para eliminar un registro del producto en la API.
   * Realiza una solicitud HTTP DELETE para eliminar el producto basado en su ID.
   *
   * @param product - Objeto que contiene la información del producto con los cambios a ser actualizados.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la eliminación del producto.
   */
  delete(product: IProduct): Promise<any> {
    return firstValueFrom(
      this.http.delete(`${environment.apiUrl + this.url}/${product.id}`)
    );
  }

  /**
   * Crea un objeto `FormData` a partir de un producto.
   *
   * Este método construye un objeto `FormData` que contiene los campos del producto,
   * excluyendo aquellos que son `null` o `undefined`. Además, si el producto tiene
   * un archivo de imagen asociado, se añade al `FormData`.
   *
   * @param product - El objeto del producto que contiene los campos a incluir en el `FormData`.
   *
   * @returns El objeto `FormData` construido con los datos del producto.
   */
  private buildFormData(product: IProduct): FormData {
    const formData = new FormData();
    const fields: (keyof IProduct)[] = ['name', 'description', 'disabled'];

    fields.forEach((field) => {
      const value = product[field];
      if (value !== null && value !== undefined) {
        formData.append(field, String(value));
      }
    });

    if (product.imageFile) {
      formData.append('image', product.imageFile);
    }

    return formData;
  }
}
